import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { authAPI } from '../api/endpoints';
import './Auth.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Animated "constellation" background (ported from the brand 404 page).
function useConstellation(ref: React.RefObject<HTMLCanvasElement>) {
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const CFG = {
      density: 0.00005, maxPoints: 100, minPoints: 30, speed: 0.18, drift: 0.1,
      linkDistance: 140, linkWidth: 1.4, bg: '#1e2757',
      dotColor: 'rgba(237, 244, 137, 0.85)', dotRadius: [1.2, 2.5] as [number, number],
    };
    let dpr = 1, w = 0, h = 0, raf = 0;
    let points: { x: number; y: number; vx: number; vy: number; r: number }[] = [];
    const rand = (min: number, max: number) => min + Math.random() * (max - min);
    const makePoint = (randomPos = false) => {
      const angle = rand(0, Math.PI * 2), sp = CFG.speed * rand(0.6, 1.4);
      return {
        x: randomPos ? rand(0, w) : w / 2, y: randomPos ? rand(0, h) : h / 2,
        vx: Math.cos(angle) * sp, vy: Math.sin(angle) * sp,
        r: rand(CFG.dotRadius[0], CFG.dotRadius[1]),
      };
    };
    const resize = () => {
      dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      w = Math.floor(window.innerWidth); h = Math.floor(window.innerHeight);
      canvas.width = Math.floor(w * dpr); canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + 'px'; canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const target = Math.max(CFG.minPoints, Math.min(CFG.maxPoints, Math.round(w * h * CFG.density)));
      if (points.length > target) points.length = target;
      while (points.length < target) points.push(makePoint(true));
    };
    const step = () => {
      ctx.fillStyle = CFG.bg; ctx.fillRect(0, 0, w, h);
      for (const p of points) {
        p.vx += rand(-CFG.drift, CFG.drift) * 0.01; p.vy += rand(-CFG.drift, CFG.drift) * 0.01;
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) { p.x = 0; p.vx *= -1; } if (p.x > w) { p.x = w; p.vx *= -1; }
        if (p.y < 0) { p.y = 0; p.vy *= -1; } if (p.y > h) { p.y = h; p.vy *= -1; }
      }
      for (let i = 0; i < points.length; i++) {
        for (let j = i + 1; j < points.length; j++) {
          const a = points[i], b = points[j];
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < CFG.linkDistance) {
            const t = 1 - dist / CFG.linkDistance;
            ctx.strokeStyle = `rgba(237, 244, 137, ${(0.06 + t * 0.3).toFixed(3)})`;
            ctx.lineWidth = CFG.linkWidth * (0.3 + t);
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
      }
      ctx.fillStyle = CFG.dotColor;
      for (const p of points) { ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill(); }
      raf = requestAnimationFrame(step);
    };
    window.addEventListener('resize', resize, { passive: true });
    resize(); raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, [ref]);
}

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const navigate = useNavigate();
  const auth = React.useContext(AuthContext);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useConstellation(canvasRef);

  useEffect(() => {
    const ssoError = new URLSearchParams(window.location.search).get('sso_error');
    if (ssoError) setError(ssoError);
    authAPI.ssoStatus().then((r) => setSsoEnabled(!!r.data?.enabled)).catch(() => {});
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await authAPI.login(email, password);
      const { token, user } = response.data;
      auth.login(user, token);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleSso = () => {
    window.location.href = `${API_BASE}/auth/sso/login`;
  };

  return (
    <div className="auth-container">
      <canvas ref={canvasRef} className="auth-bg" />
      <div className="auth-content">
        <div className="auth-logo">
          <img
            src="https://offener-prozess.de/wp-content/uploads/Logo_OP_DZ_blau.svg"
            alt="Offener Prozess"
          />
        </div>
        <div className="auth-box">
          <h1>Display CMS</h1>
          <p className="auth-sub">Bitte anmelden, um fortzufahren.</p>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>E-Mail</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            </div>
            <div className="form-group">
              <label>Passwort</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" disabled={loading}>
              {loading ? 'Anmelden …' : 'Anmelden'}
            </button>
          </form>
          {ssoEnabled && (
            <>
              <div className="auth-divider"><span>oder</span></div>
              <button type="button" className="btn-sso" onClick={handleSso}>
                Mit SSO anmelden
              </button>
            </>
          )}
        </div>
        <div className="auth-footer">
          <div className="auth-footer-title">Offener Prozess gGmbH</div>
          <div>Ein Dokumentationszentrum zum NSU-Komplex in Sachsen</div>
        </div>
      </div>
    </div>
  );
};
